import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';
import { stateNameForCode } from '../utils/countriesStates';
import OrgPrintChrome from './OrgPrintChrome';
import { NOTE_REASONS } from './SaleNoteModal';
import { GstRegistrationBadges, GstRegistrationModal, GstRegistrationPrintBlock, useGstEnabled, useGstSummaries } from './GstRegistration';
import HistoryButton from './HistoryButton';
import { formatDate } from '../utils/dateFormat';

const money = (currency, value) => `${currency} ${(parseFloat(value) || 0).toFixed(2)}`;

/** Credit and debit notes issued against sale bills: list, plus a printable view of each note. */
function SaleNotes({ active, refreshKey }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('');
  const [viewing, setViewing] = useState(null);
  const [applying, setApplying] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [gstDialog, setGstDialog] = useState(null);
  const gstEnabled = useGstEnabled(active);
  const [gstSummaries, reloadGstSummaries] = useGstSummaries('SALE_NOTE', notes.map((n) => n.id), active);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    axios.get('/api/sale-notes')
      .then((r) => { if (!cancelled) setNotes(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) toast.error('Error fetching credit / debit notes'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, refreshKey, reloadKey]);

  useEffect(() => {
    if (!active || !user?.organizationId) return;
    axios.get(`/api/organizations/${user.organizationId}`)
      .then((r) => setCurrency(r.data?.defaultCurrency || ''))
      .catch(() => {});
  }, [active, user?.organizationId]);

  const typeBadge = (type) => <Badge bg={type === 'CREDIT' ? 'danger' : 'primary'}>{type === 'CREDIT' ? 'Credit' : 'Debit'}</Badge>;

  return (
    <div className="mt-3">
      <p className="text-muted">
        Credit notes (returns, discounts) and debit notes (extra charges) issued against sale bills. To issue one, use the
        Credit note / Debit note buttons on an issued bill in the Sale Bill tab.
      </p>
      {loading ? (
        <div className="d-flex justify-content-center"><Spinner animation="border" role="status" /></div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Note</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Bill</th>
                  <th>Customer</th>
                  <th>Reason</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">Tax</th>
                  <th className="text-end">Total</th>
                  <th className="text-end">Held as credit</th>
                  <th>Stock</th>
                  <th>e-Invoice</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {notes.map((n) => (
                  <tr key={n.id}>
                    <td><strong>{n.noteNumber}</strong></td>
                    <td>{typeBadge(n.noteType)}</td>
                    <td>{formatDate(n.noteDate)}</td>
                    <td>{n.saleBillNumber}</td>
                    <td>{n.customerName || '-'}</td>
                    <td>{NOTE_REASONS[n.reason] || n.reason}</td>
                    <td className="text-end">{money(currency, n.taxableAmount)}</td>
                    <td className="text-end">{money(currency, n.totalTaxAmount)}</td>
                    <td className="text-end">{money(currency, n.totalAmount)}</td>
                    <td className="text-end">{parseFloat(n.unappliedAmount) > 0 ? money(currency, n.unappliedAmount) : '-'}</td>
                    <td>{n.returnGrnNumber ? `Returned (${n.returnGrnNumber})` : '-'}</td>
                    <td><GstRegistrationBadges summary={gstSummaries[n.id]} /></td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-info" className="me-2" onClick={() => setViewing(n)}>View / Print</Button>
                      {(gstEnabled.einvoiceEnabled || gstSummaries[n.id]) && (
                        <Button size="sm" variant="outline-success" className="me-2" onClick={() => setGstDialog(n)}>e-Invoice</Button>
                      )}
                      {parseFloat(n.unappliedAmount) > 0 && (
                        <Button size="sm" variant="outline-success" onClick={() => setApplying(n)}>Apply credit</Button>
                      )}
                      <HistoryButton documentType="SaleNote" documentId={n.id} label={`Note ${n.noteNumber}`} className="ms-1" />
                    </td>
                  </tr>
                ))}
                {notes.length === 0 && (
                  <tr><td colSpan="13" className="text-center text-muted">No credit or debit notes yet.</td></tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <GstRegistrationModal
        show={Boolean(gstDialog)}
        docType="SALE_NOTE"
        docId={gstDialog?.id}
        label={gstDialog?.noteNumber}
        onHide={() => setGstDialog(null)}
        canCancel={hasAnyRole(user, ['ORG_ADMIN', 'SALES_MGR'])}
        onChanged={reloadGstSummaries}
      />

      <ApplyCreditModal
        note={applying}
        currency={currency}
        onHide={() => setApplying(null)}
        onApplied={() => setReloadKey((k) => k + 1)}
      />

      <Modal show={Boolean(viewing)} onHide={() => setViewing(null)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>{viewing?.noteType === 'CREDIT' ? 'Credit Note' : 'Debit Note'}</Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={() => window.print()} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setViewing(null)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
            {viewing && <NotePrint note={viewing} currency={currency} />}
          </OrgPrintChrome>
        </Modal.Body>
      </Modal>
    </div>
  );
}

/** Spends the credit a credit note holds on the customer's other open bills, oldest due first. */
function ApplyCreditModal({ note, currency, onHide, onApplied }) {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [amounts, setAmounts] = useState({});

  useEffect(() => {
    if (!note) return;
    let cancelled = false;
    setLoading(true);
    setAmounts({});
    axios.get(`/api/sale-bills/customer/${note.customerId}`)
      .then((r) => {
        if (cancelled) return;
        const open = (Array.isArray(r.data) ? r.data : [])
          .filter((b) => b.status === 'ISSUED' && parseFloat(b.balanceDue) > 0)
          .sort((a, b) => String(a.dueDate || a.billDate).localeCompare(String(b.dueDate || b.billDate)));
        setBills(open);
      })
      .catch(() => { if (!cancelled) toast.error('Could not load the customer\u2019s bills'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [note]);

  const held = note ? parseFloat(note.unappliedAmount) || 0 : 0;
  const entered = (id) => parseFloat(amounts[id]) || 0;
  const total = bills.reduce((sum, b) => sum + entered(b.id), 0);

  const fillOldestFirst = () => {
    let left = held;
    const next = {};
    bills.forEach((b) => {
      const use = Math.min(left, parseFloat(b.balanceDue));
      if (use > 0) {
        next[b.id] = use.toFixed(2);
        left -= use;
      }
    });
    setAmounts(next);
  };

  const submit = async () => {
    const applications = bills
      .filter((b) => entered(b.id) > 0)
      .map((b) => ({ saleBillId: b.id, appliedAmount: entered(b.id) }));
    if (applications.length === 0) {
      toast.error('Enter an amount for at least one bill');
      return;
    }
    try {
      setSaving(true);
      await axios.post(`/api/sale-notes/${note.id}/apply`, { applications });
      toast.success('Credit applied');
      onApplied();
      onHide();
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === 'string' && data ? data : 'Failed to apply the credit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={Boolean(note)} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Apply credit{note ? ` — ${note.noteNumber}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {note && (
          <Alert variant="info" className="py-2 small">
            {note.customerName} holds {money(currency, held)} from this credit note. Choose which open bills it should settle.
            This only decides which bills the credit pays off; the customer&apos;s account balance doesn&apos;t change.
          </Alert>
        )}
        {loading ? (
          <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
        ) : bills.length === 0 ? (
          <p className="text-muted mb-0">This customer has no open bills to apply the credit to.</p>
        ) : (
          <>
            <div className="d-flex justify-content-end mb-2">
              <Button size="sm" variant="outline-secondary" onClick={fillOldestFirst}>Fill oldest first</Button>
            </div>
            <Table bordered size="sm">
              <thead>
                <tr><th>Bill</th><th>Due</th><th className="text-end">Balance due</th><th style={{ width: 160 }}>Apply</th></tr>
              </thead>
              <tbody>
                {bills.map((b) => (
                  <tr key={b.id}>
                    <td>{b.billNumber}</td>
                    <td>{formatDate(b.dueDate) || '-'}</td>
                    <td className="text-end">{money(currency, b.balanceDue)}</td>
                    <td>
                      <Form.Control size="sm" type="number" min="0" step="0.01" max={b.balanceDue}
                        value={amounts[b.id] || ''} onChange={(e) => setAmounts((prev) => ({ ...prev, [b.id]: e.target.value }))} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className={`text-end ${total > held + 0.001 ? 'text-danger' : ''}`}>
              Applying {money(currency, total)} of {money(currency, held)}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="success" onClick={submit} disabled={saving || loading || bills.length === 0 || total <= 0}>
          {saving ? 'Applying...' : 'Apply credit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function NotePrint({ note, currency }) {
  const credit = note.noteType === 'CREDIT';
  const placeName = note.placeOfSupplyStateCode ? stateNameForCode(note.placeOfSupplyStateCode) : null;
  return (
    <div className="print-content">
      <div className="text-center mb-4">
        <h2>{credit ? 'CREDIT NOTE' : 'DEBIT NOTE'}</h2>
        <p className="text-muted"><strong>{note.noteNumber}</strong></p>
      </div>
      <Row className="mb-3">
        <Col md={6}>
          <p><strong>Customer:</strong> {note.customerName || '-'}</p>
          {note.customerGstin && <p><strong>Buyer GSTIN:</strong> {note.customerGstin}</p>}
          <p><strong>Against bill:</strong> {note.saleBillNumber} dated {formatDate(note.saleBillDate)}</p>
          <p><strong>Reason:</strong> {NOTE_REASONS[note.reason] || note.reason}</p>
        </Col>
        <Col md={6}>
          <p><strong>Note date:</strong> {formatDate(note.noteDate)}</p>
          {note.supplierGstin && <p><strong>Supplier GSTIN:</strong> {note.supplierGstin}</p>}
          {note.placeOfSupplyStateCode && (
            <p><strong>Place of supply:</strong> {placeName ? `${placeName} (${note.placeOfSupplyStateCode})` : note.placeOfSupplyStateCode}</p>
          )}
        </Col>
      </Row>
      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>Product</th>
            <th>HSN</th>
            <th className="text-end">Qty</th>
            <th className="text-end">Taxable</th>
            <th className="text-end">Rate %</th>
            {note.isInterState ? <th className="text-end">IGST</th> : <><th className="text-end">CGST</th><th className="text-end">SGST</th></>}
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {(note.lines || []).map((l) => (
            <tr key={l.id}>
              <td>{l.productName || '-'}</td>
              <td>{l.hsnCode || '-'}</td>
              <td className="text-end">{l.quantity != null ? `${l.quantity}${l.unitSymbol ? ` ${l.unitSymbol}` : ''}` : '-'}</td>
              <td className="text-end">{money(currency, l.taxableAmount)}</td>
              <td className="text-end">{l.taxRatePercent ?? 0}</td>
              {note.isInterState
                ? <td className="text-end">{money(currency, l.igstAmount)}</td>
                : <><td className="text-end">{money(currency, l.cgstAmount)}</td><td className="text-end">{money(currency, l.sgstAmount)}</td></>}
              <td className="text-end">{money(currency, l.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex justify-content-end">
        <Table borderless size="sm" style={{ width: 320 }}>
          <tbody>
            <tr><td>Taxable amount</td><td className="text-end">{money(currency, note.taxableAmount)}</td></tr>
            {note.isInterState ? (
              <tr><td>IGST</td><td className="text-end">{money(currency, note.igstAmount)}</td></tr>
            ) : (
              <>
                <tr><td>CGST</td><td className="text-end">{money(currency, note.cgstAmount)}</td></tr>
                <tr><td>SGST</td><td className="text-end">{money(currency, note.sgstAmount)}</td></tr>
              </>
            )}
            <tr className="fw-bold border-top"><td>Note total</td><td className="text-end">{money(currency, note.totalAmount)}</td></tr>
          </tbody>
        </Table>
      </div>
      {credit && parseFloat(note.unappliedAmount) > 0 && (
        <p className="small">
          {money(currency, note.unappliedAmount)} of this credit was more than the bill still owed, and is held as credit for the customer.
        </p>
      )}
      {(note.applications || []).length > 0 && (
        <p className="small mb-1">
          Held credit applied to: {note.applications.map((a) => `${a.saleBillNumber} (${money(currency, a.appliedAmount)} on ${a.appliedOn})`).join(', ')}.
        </p>
      )}
      {note.notes && <p className="mt-3"><strong>Remarks:</strong> {note.notes}</p>}
      <GstRegistrationPrintBlock docType="SALE_NOTE" docId={note.id} />
    </div>
  );
}

export default SaleNotes;
